import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { INTERESTS } from "@/data/content";
import { trackLead } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", company: "", interest: "general", message: "" };

export default function ContactForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      trackLead({ interest: form.interest, company: form.company });
      setDone(true);
      setForm(EMPTY);
      toast.success("Transmission received. We'll be in touch.");
    } catch (err) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/15 py-3.5 text-white placeholder:text-slate-600 focus:border-[#22D3EE] focus:outline-none transition-colors duration-300 text-sm";

  return (
    <section id="contact" className="relative py-28 lg:py-40 bg-[#07070E] border-t border-white/10 overflow-hidden" data-testid="contact-section">
      <div className="absolute -top-40 right-0 w-[500px] h-[500px] radial-cyan" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <span className="text-xs uppercase tracking-[0.3em] text-[#22D3EE]">Request a demo</span>
          <h2 className="mt-5 font-display font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-none">
            Step inside<br />the sphere
          </h2>
          <p className="mt-8 max-w-md text-base text-slate-400 leading-relaxed">
            Tell us what you're protecting. Our team will map a defense and delivery
            strategy tailored to your infrastructure.
          </p>
          <div className="mt-12 space-y-3 font-mono text-sm text-slate-500">
            <p><span className="text-[#22D3EE]">▸</span> Secure@endasphere.com</p>
            <p><span className="text-[#22D3EE]">▸</span> 24/7 threat operations</p>
            <p><span className="text-[#22D3EE]">▸</span> 312 edge locations worldwide</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="rounded-2xl border border-white/10 bg-[#0A0A12]/80 backdrop-blur-xl p-8 lg:p-10"
        >
          {done ? (
            <div className="min-h-[420px] flex flex-col items-center justify-center text-center" data-testid="contact-success">
              <div className="w-14 h-14 rounded-full border border-[#22D3EE] flex items-center justify-center drop-glow mb-6">
                <ArrowUpRight className="w-6 h-6 text-[#22D3EE]" />
              </div>
              <h3 className="font-display font-700 text-2xl text-white">You're in the sphere.</h3>
              <p className="mt-3 text-sm text-slate-400 max-w-xs">
                We received your request and will respond within one business day.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="contact-reset"
                className="mt-8 text-xs uppercase tracking-[0.2em] text-[#22D3EE] hover:text-white transition-colors"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Name *</label>
                  <input data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="Ada Lovelace" className={inputCls} />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Email *</label>
                  <input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} placeholder="ada@company.io" className={inputCls} />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Company</label>
                <input data-testid="contact-company" value={form.company} onChange={update("company")} placeholder="Origin Corp" className={inputCls} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Interest</label>
                <select data-testid="contact-interest" value={form.interest} onChange={update("interest")} className={`${inputCls} [&>option]:bg-[#0A0A12]`}>
                  {INTERESTS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Message *</label>
                <textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={3} placeholder="What are you protecting?" className={`${inputCls} resize-none`} />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#22D3EE] px-7 py-4 text-sm font-700 uppercase tracking-[0.12em] text-[#05050A] hover:bg-white transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Transmit request <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" /></>}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
